import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const CATEGORY_LABELS = {
  lighting_visibility: 'Lighting & Visibility',
  engine_fluids: 'Engine & Fluids',
  mechanical_systems: 'Mechanical Systems',
  tires_movement: 'Tires & Movement',
  tow_equipment: 'Tow Equipment',
  safety_equipment: 'Safety Equipment',
  condition_warnings: 'Condition & Warnings',
  dashboard_warnings: 'Dashboard Warning Lights',
};

const CATEGORY_ORDER = [
  'lighting_visibility', 'engine_fluids', 'mechanical_systems', 'tires_movement',
  'tow_equipment', 'safety_equipment', 'condition_warnings', 'dashboard_warnings',
];

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
    return d.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
    return d.toLocaleDateString('en-US', {
      timeZone: 'America/Chicago',
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getResultDisplay(responseType, result) {
  switch (responseType) {
    case 'pass_fail':
      return result === 'pass'
        ? { icon: '\u2713', text: 'Pass', className: 'pass' }
        : { icon: '\u2717', text: 'Fail', className: 'fail' };
    case 'ok_low':
      return result === 'ok'
        ? { icon: '\u2713', text: 'OK', className: 'pass' }
        : { icon: '\u26A0', text: 'Low', className: 'warn' };
    case 'yes_no':
      return result === 'no'
        ? { icon: '\u2713', text: 'No', className: 'pass' }
        : { icon: '\u26A0', text: 'Yes', className: 'fail' };
    default:
      return result === 'pass' || result === 'ok' || result === 'no'
        ? { icon: '\u2713', text: result, className: 'pass' }
        : { icon: '\u2717', text: result, className: 'fail' };
  }
}

function isNegativeResult(responseType, result) {
  if (responseType === 'pass_fail') return result === 'fail';
  if (responseType === 'ok_low') return result === 'low';
  if (responseType === 'yes_no') return result === 'yes';
  return result === 'fail' || result === 'low' || result === 'yes';
}

export default function InspectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAtLeastAdmin } = useAuth();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const isAdmin = isAtLeastAdmin;

  useEffect(() => {
    async function load() {
      try {
        const data = await api.getInspection(id);
        setInspection(data);
      } catch (err) {
        setError(err.message || 'Failed to load inspection');
      }
      setLoading(false);
    }
    load();
  }, [id]);

  async function handleMarkReviewed() {
    setReviewing(true);
    try {
      await api.reviewInspection(id);
      const data = await api.getInspection(id);
      setInspection(data);
    } catch (err) {
      setError(err.message || 'Failed to mark as reviewed');
    }
    setReviewing(false);
  }

  if (loading) {
    return <div className="inspection-detail"><div className="loading-spinner">Loading inspection...</div></div>;
  }

  if (error && !inspection) {
    return (
      <div className="inspection-detail">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-outline" onClick={() => navigate('/maintenance/inspections')}>
          &larr; Back to Inspections
        </button>
      </div>
    );
  }

  if (!inspection) return null;

  const responses = inspection.responses || [];
  const photos = inspection.photos || [];

  // Group responses by category
  const grouped = {};
  for (const resp of responses) {
    const cat = resp.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(resp);
  }

  // Build ordered categories list (known order first, then any extras)
  const orderedCategories = [
    ...CATEGORY_ORDER.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)),
  ];

  // Count pass/total
  const totalCount = responses.length;
  const passCount = responses.filter(r => !isNegativeResult(r.response_type, r.result)).length;

  // Get photos for a specific item
  function getItemPhotos(itemId) {
    return photos.filter(p => p.item_id === itemId);
  }

  // Parse warning lights
  let warningLights = null;
  if (inspection.warning_lights_detail) {
    try {
      warningLights = typeof inspection.warning_lights_detail === 'string'
        ? JSON.parse(inspection.warning_lights_detail)
        : inspection.warning_lights_detail;
    } catch {
      warningLights = null;
    }
  }

  // Get photos without an item_id (general / warning light photos)
  const warningLightPhotos = photos.filter(p => !p.item_id || p.context === 'warning_light');

  return (
    <div className="inspection-detail">
      {/* Header */}
      <div className="inspection-detail__header">
        <div className="inspection-detail__header-left">
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/maintenance/inspections')}>
            &larr; Back to Inspections
          </button>
          <h1>
            Inspection #{inspection.id}
            {inspection.truck_unit_number && ` — Truck #${inspection.truck_unit_number}`}
          </h1>
        </div>
        <div className="inspection-detail__header-right">
          <span className={`status-badge status-badge--${inspection.status} status-badge--lg`}>
            {inspection.status === 'submitted' ? 'Submitted' :
             inspection.status === 'flagged' ? 'Flagged' :
             inspection.status === 'reviewed' ? 'Reviewed' : inspection.status}
          </span>
          {isAdmin && inspection.status !== 'reviewed' && (
            <button
              className="btn btn-gold"
              onClick={handleMarkReviewed}
              disabled={reviewing}
            >
              {reviewing ? 'Saving...' : 'Mark as Reviewed'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* General Info Card */}
      <div className="inspection-detail__card">
        <h2 className="inspection-detail__card-title">General Information</h2>
        <div className="inspection-detail__info-grid">
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Truck</span>
            <span className="inspection-detail__info-value">
              #{inspection.truck_unit_number || '—'}
            </span>
          </div>
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Driver</span>
            <span className="inspection-detail__info-value">
              {inspection.driver_name || '—'}
            </span>
          </div>
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Shift</span>
            <span className="inspection-detail__info-value" style={{ textTransform: 'capitalize' }}>
              {inspection.shift_type || '—'}
            </span>
          </div>
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Date</span>
            <span className="inspection-detail__info-value">
              {formatDate(inspection.shift_date)}
            </span>
          </div>
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Odometer</span>
            <span className="inspection-detail__info-value">
              {inspection.odometer ? Number(inspection.odometer).toLocaleString() + ' mi' : '—'}
            </span>
          </div>
          <div className="inspection-detail__info-item">
            <span className="inspection-detail__info-label">Submitted At</span>
            <span className="inspection-detail__info-value">
              {formatDateTime(inspection.submitted_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Checklist Results Card */}
      <div className="inspection-detail__card">
        <h2 className="inspection-detail__card-title">
          Inspection Results
          <span className="inspection-detail__results-summary">
            {passCount}/{totalCount} Pass
          </span>
        </h2>

        <div className="inspection-detail__checklist">
          {orderedCategories.map(category => (
            <div key={category}>
              <div className="inspection-form__divider">
                {CATEGORY_LABELS[category] || category}
              </div>
              {grouped[category].map(resp => {
                const display = getResultDisplay(resp.response_type, resp.result);
                const negative = isNegativeResult(resp.response_type, resp.result);
                const itemPhotos = getItemPhotos(resp.item_id);

                return (
                  <div
                    key={resp.id || resp.item_id}
                    className={`inspection-detail__checklist-row${negative ? ' fail' : ''}`}
                  >
                    <span className={`inspection-detail__checklist-icon ${display.className}`}>
                      {display.icon}
                    </span>
                    <span className="inspection-detail__checklist-name">
                      {resp.item_name}
                    </span>
                    <span className={`inspection-detail__checklist-icon ${display.className}`}
                      style={{ marginLeft: 'auto', fontWeight: 600, fontSize: '0.85rem' }}
                    >
                      {display.text}
                    </span>

                    {negative && resp.notes && (
                      <div className="inspection-detail__fail-notes">
                        <strong>Notes:</strong> {resp.notes}
                      </div>
                    )}

                    {itemPhotos.length > 0 && (
                      <div className="inspection-detail__photos">
                        {itemPhotos.map((photo, idx) => (
                          <a
                            key={photo.id || idx}
                            href={photo.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inspection-detail__photo-thumb"
                          >
                            <img src={photo.file_path} alt={`Photo ${idx + 1}`} />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Dashboard Warning Lights Card */}
      {warningLights && (
        <div className="inspection-detail__card">
          <h2 className="inspection-detail__card-title">Dashboard Warning Lights</h2>
          {(Array.isArray(warningLights) ? warningLights : [warningLights]).map((light, idx) => (
            <div key={idx} style={{ marginBottom: idx < (Array.isArray(warningLights) ? warningLights.length : 1) - 1 ? '12px' : 0 }}>
              {light.description && (
                <p style={{ margin: '0 0 6px' }}>{light.description}</p>
              )}
              {light.severity && (
                <span className={`status-badge status-badge--${light.severity === 'critical' ? 'flagged' : light.severity === 'warning' ? 'submitted' : 'reviewed'}`}>
                  {light.severity}
                </span>
              )}
            </div>
          ))}
          {warningLightPhotos.length > 0 && (
            <div className="inspection-detail__photos" style={{ marginTop: '10px' }}>
              {warningLightPhotos.map((photo, idx) => (
                <a
                  key={photo.id || idx}
                  href={photo.file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inspection-detail__photo-thumb"
                >
                  <img src={photo.file_path} alt={`Warning light photo ${idx + 1}`} />
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Body Damage Card */}
      {inspection.has_body_damage === 1 && (
        <div className="inspection-detail__card">
          <h2 className="inspection-detail__card-title">Body Damage</h2>
          {inspection.body_damage_notes && (
            <div className="inspection-detail__fail-notes">
              <strong>Notes:</strong> {inspection.body_damage_notes}
            </div>
          )}
        </div>
      )}

      {/* Comments Card */}
      {inspection.comments && (
        <div className="inspection-detail__card">
          <h2 className="inspection-detail__card-title">Additional Comments</h2>
          <p className="inspection-detail__comments">{inspection.comments}</p>
        </div>
      )}

      {/* Review Info Card */}
      {inspection.status === 'reviewed' && (
        <div className="inspection-detail__card">
          <h2 className="inspection-detail__card-title">Review Information</h2>
          <div className="inspection-detail__info-grid">
            <div className="inspection-detail__info-item">
              <span className="inspection-detail__info-label">Reviewed By</span>
              <span className="inspection-detail__info-value">
                {inspection.reviewed_by_name || '—'}
              </span>
            </div>
            <div className="inspection-detail__info-item">
              <span className="inspection-detail__info-label">Reviewed At</span>
              <span className="inspection-detail__info-value">
                {formatDateTime(inspection.reviewed_at)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
